import React, {
  CSSProperties,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import {useSelector} from 'react-redux';
import {UploadServiceProvider} from '@taopix/taopix-asset-manager';
import {
  AssetProcessorInterface,
  UploadOptions,
} from '@taopix/taopix-asset-manager/dist/types/types';
import {AssetServicesSelector, LayoutSelector} from '../../../Selectors';
import {useTaopix} from '../TaopixContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import useSettings from '../../../Hooks/useSettings';
import {AssetPart} from '@taopix/taopix-core';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {COLOUR_COMPONENT_FLATTENED} from '../../../Constants';
import {AssetService as UploadAssetService} from '@taopix/taopix-asset-manager/dist/types/UploadServiceProvider';
import AssetContext from '@taopix/taopix-asset-manager/dist/types/Context/AssetContext';
import {TaopixServiceEventMap} from '@taopix/taopix-asset-manager/dist/types/Services/TaopixService';

// create the context
const UploadContext = createContext<UploadServiceProvider>({} as UploadServiceProvider);
const DropZoneContext = createContext<UploadServiceProvider>({} as UploadServiceProvider);

/**
 * Creates an instance of the Taopix Uploader, and will provide it
 * to any child components that require it.
 *
 * @param param0
 * @returns
 */
const AssetUploadProvider = ({children}: {children: any}) => {
  const taopix = useTaopix();

  // pull in the settings
  const settings = useSettings();

  // pull in the experience settings
  const {experience, getAIMode} = useExperience();

  // pull in the product layout
  const layout = useSelector(LayoutSelector);

  // pull in the asset services
  const assetServices = useSelector(AssetServicesSelector);

  // create the state
  const [uploader, setUploader] = useState<UploadServiceProvider>({} as UploadServiceProvider);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!layout) return;
    if (Object.keys(uploader).length > 0) return;

    const {imageScaling} = experience.SETTINGS;

    const options: UploadOptions = {
      currentProjectFileSize: 0,
      min: 1,
      max: 250,
      imageScalingBeforeMP: imageScaling.scaleBeforeUpload
        ? imageScaling.maxScaleBeforeUploadMP
        : 0,
      maxUploadMP: imageScaling.scaleAfterUpload ? imageScaling.maxScaleAfterUploadMP : 0,
      maxThumbnailSize: 300,
      aiMode: getAIMode(), //layout.features.aimode,
      useDropZone: false,
      container: undefined,
      pubKey: `${window.config.pubKey}`,
      api: `${window.config.baseURL}`,
      batchRef: settings.state.misc.batchRef,
      token: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-token`
      ),
      refreshToken: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-refreshToken`
      ),
      designerId: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-uuid`
      ),
      ref: settings.state.tokens.session,
      createThumbnails: true,
      monitor: {
        interval: 5000,
      },
      validation: {
        allowpicturejpegrgb: settings.state.features.imageTypes.jpg?.rgb || false,
        allowpicturejpegcmyk: settings.state.features.imageTypes.jpg?.cmyk || false,
        allowpicturejpeggray: settings.state.features.imageTypes.jpg?.gray || false,
        allowpicturetiffrgb: settings.state.features.imageTypes.tiff.rgb,
        allowpicturetiffcmyk: settings.state.features.imageTypes.tiff.cmyk,
        allowpicturetiffgray: settings.state.features.imageTypes.tiff.gray,
        allowpicturepng: settings.state.features.imageTypes.png,
      },
      serverSettings: settings.state.imageServers,
    };
    setUploader(new UploadServiceProvider(options, assetServices as UploadAssetService[]));
  }, [taopix.state.loaded]);

  return <UploadContext.Provider value={uploader}>{children}</UploadContext.Provider>;
};

const DropZoneProvider = ({
  children,
  container,
  catchZone = {zIndex: 100, content: <></>, classList: '', style: {} as CSSProperties},
}: {
  children: any;
  container: HTMLElement | string;
  catchZone?: {
    zIndex?: number;
    content?: JSX.Element;
    classList?: string;
    style?: CSSProperties;
  };
}) => {
  const app = useTaopix();

  // pull in the settings
  const settings = useSettings();

  // pull in the experience settings
  const {experience, getAIMode} = useExperience();

  // pull in the product layout
  const layout = useSelector(LayoutSelector);

  // pull in the asset services
  const assetServices = useSelector(AssetServicesSelector);

  // create the state
  const [uploader, setUploader] = useState<UploadServiceProvider>({} as UploadServiceProvider);

  // Store the processor, the drag and drop processor should not be constantly destroyed.
  const assetProcessor = useRef({} as AssetProcessorInterface<AssetContext, TaopixServiceEventMap>);

  const onUpdate = useCallback(event => {
    const updates = event.detail;

    // update each of the assets
    updates.forEach(update => {
      // the page state manager gives us an easy way of fetching an asset from the store
      const asset = app.core.get('stateManager').getAsset(update.id as number);
      if (!asset) {
        return;
      }

      // if this is an AI update, apply the meta data change
      if (update?.metaData) {
        const {status, regions} = update.metaData as any;
        // this is an AI update
        return app.core.get('stateManager').updateAsset({
          ...asset,
          metaData: {
            ...asset.metaData,
            status: status,
            regions: (regions as unknown as string).split(/\r/) || [],
          } as any,
        });
      }

      // get a flattened FPO to use for the thumbnail
      const thumbsrc =
        update?.children?.find(
          c =>
            c &&
            c?.part === AssetPart.Intermediate &&
            c?.colourComponent === COLOUR_COMPONENT_FLATTENED
        )?.src || '';

      if ('' !== asset.blobUrl) {
        // eslint-disable-next-line node/no-unsupported-features/node-builtins
        URL.revokeObjectURL(asset.blobUrl);
      }

      // update the asset
      app.core.get('stateManager').updateAsset({
        ...asset,
        ...(update as unknown as AssetMetaInterface),
        src: thumbsrc,
        thumbnailsrc: thumbsrc,
        hiressrc: thumbsrc,
        blobUrl: '',
      });
    });
  }, []);

  useEffect(() => {
    if (!layout) return;
    if (Object.keys(uploader).length > 0) return;

    // Destroy the current processor if there is one.
    if (assetProcessor.current.hasOwnProperty('destroy')) {
      (assetProcessor.current as any).destroy({code: '%TAOPIXONLINE%'});
    }

    const {imageScaling} = experience.SETTINGS;
    const options: UploadOptions = {
      chunkSize: 20,
      currentProjectFileSize: 0,
      min: 1,
      max: 250,
      imageScalingBeforeMP: imageScaling.scaleBeforeUpload
        ? imageScaling.maxScaleBeforeUploadMP
        : 0,
      maxUploadMP: imageScaling.scaleAfterUpload ? imageScaling.maxScaleAfterUploadMP : 0,
      maxThumbnailSize: 300,
      aiMode: getAIMode(), //layout.features.aimode,
      useDropZone: true,
      container: container,
      pubKey: `${window.config.pubKey}`,
      api: `${window.config.baseURL}`,
      batchRef: settings.state.misc.batchRef,
      token: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-token`
      ),
      refreshToken: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-refreshToken`
      ),
      designerId: window.Taopix.Core.get('states').sessionStorage.getItem(
        `${settings.state.tokens.session}-uuid`
      ),
      ref: settings.state.tokens.session,
      createThumbnails: true,
      monitor: {
        interval: 5000,
      },
      validation: {
        allowpicturejpegrgb: settings.state.features.imageTypes.jpg?.rgb || false,
        allowpicturejpegcmyk: settings.state.features.imageTypes.jpg?.cmyk || false,
        allowpicturejpeggray: settings.state.features.imageTypes.jpg?.gray || false,
        allowpicturetiffrgb: settings.state.features.imageTypes.tiff.rgb,
        allowpicturetiffcmyk: settings.state.features.imageTypes.tiff.cmyk,
        allowpicturetiffgray: settings.state.features.imageTypes.tiff.gray,
        allowpicturepng: settings.state.features.imageTypes.png,
      },
      serverSettings: settings.state.imageServers,
      catchZone: {
        zIndex: catchZone.zIndex,
        content: catchZone.content,
        classList: catchZone.classList,
        // style: catchZone.style,
      },
    };

    const stateManager = app.core.get('stateManager');
    const provider = new UploadServiceProvider(options, assetServices as UploadAssetService[]);
    const processor = provider.get(
      assetServices.find(s =>
        ['%TAOPIXONLINE%', '%UPLOADFROMDEVICE%'].includes(s.code)
      ) as UploadAssetService
    ) as AssetProcessorInterface<AssetContext, TaopixServiceEventMap>;
    processor.open();
    const onChunk = event => {
      const {chunk} = event.detail;
      // add the assets to the state
      stateManager.addAssets(
        chunk.filter(a => !a.error).map(a => a.getState() as unknown as AssetMetaInterface)
      );
    };
    processor.on('chunk', onChunk);

    processor.on('update', onUpdate);

    setUploader(provider);
    assetProcessor.current = processor;
  }, [layout, settings.state, assetServices, experience]);

  useEffect(() => {
    return () => {
      // When the component unmounts, remove the drag and drop feature.
      if (assetProcessor.current.hasOwnProperty('destroy')) {
        (assetProcessor.current as any).destroy({code: '%TAOPIXONLINE%'});
      }
    };
  }, []);

  return <DropZoneContext.Provider value={uploader}>{children}</DropZoneContext.Provider>;
};

/**
 * Returns an instance of the Taopix Uploader
 */
const useUploader = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUploader must be used within an UploadProvider');
  }
  return context;
};

const useDropZone = () => {
  const context = useContext(DropZoneContext);
  if (context === undefined) {
    throw new Error('useDropZone must be used within an DropZoneProvider');
  }
  return context;
};

export {AssetUploadProvider, useUploader, DropZoneProvider, useDropZone};
